import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'leaflet-editable'
import 'leaflet-spin'

export default function MapInput({
    areaInputs,
    setAreaInputs
}) {
    const LeafletRef = useRef()
    const mapRef = useRef()
    const drawControlRef = useRef()
    const drawnItemsRef = useRef()
    const enabledRectangleDrawingRef = useRef()
    const areaRef = useRef()
    const areaMarkerRef = useRef()
    const cursorTooltipRef = useRef()
    const heightMarkerRef = useRef()
    const widthMarkerRef = useRef()
    const prevAreaBounds = useRef()

    useEffect(() => {
        const mapCont = document.getElementById('map')
        if (!mapRef.current && mapCont) {
            mapRef.current = L.map(
                'map',
                {
                    minZoom: 1.5,
                    editable: true,
                    editOptions: { skipMiddleMarkers: true },
                    maxBounds: [[-90, -180], [90, 180]]
                }
            ).setView([0, 0], 1.5);

            L.tileLayer(
                'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                { attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' }
            ).addTo(mapRef.current);

            drawnItemsRef.current = drawnItemsRef.current ?? new L.FeatureGroup();
            mapRef.current.addLayer(drawnItemsRef.current);

            const coordsControl = L.control({ position: "bottomleft" });
            coordsControl.onAdd = function () {
                const div = L.DomUtil.create("div", "leaflet-cords-control");
                div.style.backgroundColor = "white";
                div.style.padding = '5px 10px'
                div.style.borderRadius = '10px'
                div.innerHTML = "0 : 0";
                return div;
            };
            coordsControl.addTo(mapRef.current);
            mapRef.current.on("mousemove", (e) => {
                const controlDiv = document.querySelector(".leaflet-cords-control");
                if (controlDiv && e?.latlng) {
                    controlDiv.innerHTML = `${e.latlng.lat.toFixed(5)} : ${e.latlng.lng.toFixed(5)}`;
                }
            });

            L.control.scale({ position: "bottomleft", imperial: true, metric: true }).addTo(mapRef.current);

            drawControlRef.current = new L.Control.Draw({
                draw: {
                    polyline: false,
                    polygon: false,
                    rectangle: { showArea: false },
                    circle: false,
                    marker: false,
                    circlemarker: false
                },
                edit: {
                    featureGroup: drawnItemsRef.current,
                    edit: false,
                    remove: true
                }
            });
            mapRef.current.addControl(drawControlRef.current);

            enabledRectangleDrawingRef.current = enabledRectangleDrawingRef.current ?? new L.Draw.Rectangle(
                mapRef.current,
                { ...drawControlRef.current.options.draw.rectangle, showArea: false }
            );
            enabledRectangleDrawingRef.current.enable();

            mapRef.current.on(L.Draw.Event.CREATED, handleAreaDraw);
            mapRef.current.on(L.Draw.Event.DELETED, handleClearArea);
            mapRef.current.on('zoom', handleMarkersDependingOnZoom);
        }
        return () => {
            setAreaInputs(null)
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [])
    const handleAreaDraw = (event) => {
        areaRef.current && mapRef.current.removeLayer(areaRef.current)
        areaMarkerRef.current && mapRef.current.removeLayer(areaMarkerRef.current)
        widthMarkerRef.current && mapRef.current.removeLayer(widthMarkerRef.current)
        heightMarkerRef.current && mapRef.current.removeLayer(heightMarkerRef.current)
        /*Drawed on the map*/
        if (event) {
            areaRef.current = event.layer
        }
        /*Drawed through inputs*/
        else {
            let createdBounds = [[areaInputs._northEast.lat, areaInputs._northEast.lng], [areaInputs._southWest.lat, areaInputs._southWest.lng]]
            areaRef.current = L.rectangle(createdBounds)
        }

        // if (checkAreaSolidGround()){
        // toastSolidGroundError()
        // return
        // }

        setAreaInputs(areaRef.current._bounds)

        createAreaWithDimentionsAndEvents()
    }

    const handleClearArea = () => {
        areaRef.current && mapRef.current.removeLayer(areaRef.current)
        areaMarkerRef.current && mapRef.current.removeLayer(areaMarkerRef.current)
        widthMarkerRef.current && mapRef.current.removeLayer(widthMarkerRef.current)
        heightMarkerRef.current && mapRef.current.removeLayer(heightMarkerRef.current)
        drawnItemsRef.current && drawnItemsRef.current.clearLayers()
        setAreaInputs(null)
    }

    const handleMarkersDependingOnZoom = () => {
        const isAreaDisplayed = areaRef.current && mapRef.current.hasLayer(areaRef.current)
        if (!isAreaDisplayed) return

        const zoomLevel = mapRef.current.getZoom()
        const isWidthDisplayed = widthMarkerRef.current ? mapRef.current.hasLayer(widthMarkerRef.current) : false
        const isHeightDisplayed = heightMarkerRef.current ? mapRef.current.hasLayer(heightMarkerRef.current) : false

        if (widthMarkerRef.current && heightMarkerRef.current && zoomLevel < 6) {
            widthMarkerRef.current.remove()
            heightMarkerRef.current.remove()
        }
        else if (areaMarkerRef.current && !isWidthDisplayed && !isHeightDisplayed && zoomLevel >= 6) {
            areaMarkerRef.current.remove()
            displayAreaDimentions(areaRef.current._latlngs[0])
        }
    }

    const createAreaWithDimentionsAndEvents = () => {
        prevAreaBounds.current = areaRef.current._bounds
        drawnItemsRef.current.addLayer(areaRef.current)
        mapRef.current.fitBounds(areaRef.current._bounds)
        areaRef.current.enableEdit()
        let areaCoordinates = areaRef.current._latlngs[0]

        displayAreaDimentions(areaCoordinates)

        areaRef.current.on('editable:vertex:drag', (event) => {
            const layer = event.layer
            const bounds = layer.getBounds()
            setAreaInputs(bounds)
            const areaCoordinates = orderCoordinates(bounds)
            displayAreaDimentions(areaCoordinates, true)
        })
    }

    const orderCoordinates = (bounds) => {
        /*Function implemented because on drag edit event the coordinates arrive in different order depending on wich vortex is dragged*/
        const southWest = bounds._southWest
        const northEast = bounds._northEast

        const northWest = { lat: northEast.lat, lng: southWest.lng }
        const southEast = { lat: southWest.lat, lng: northEast.lng }

        return [southWest, northWest, northEast, southEast]
    }

    const displayAreaDimentions = (areaCoordinates, refresh) => {
        const areaDimentions = calculateRectangeDimentionsInKm(areaCoordinates)
        let areaCenter = areaRef.current.getBounds().getCenter()
        const midRight = getMidPoint(areaCoordinates[2], areaCoordinates[3])
        const midTop = getMidPoint(areaCoordinates[1], areaCoordinates[2])

        if (areaMarkerRef.current && refresh) {
            areaMarkerRef.current.setLatLng(areaCenter)
                .setContent(
                    `<span style="font-weight: bold; font-size: 14px; display: inline-block; z-indez: 1; background-color: white; padding: 2px">
                        ${areaDimentions.area} km²
                    </span>`
                )

            heightMarkerRef.current.setLatLng(midRight)
                .setContent(
                    `<span style="font-weight: bold; font-size: 14px; display: inline-block; transform: rotate(-90deg); z-indez: 1; background-color: white; padding: 2px">
                        ${areaDimentions.height} km
                    </span>`
                )

            widthMarkerRef.current.setLatLng(midTop)
                .setContent(
                    `<span style="font-weight: bold; font-size: 14px; display: inline-block; z-indez: 1; background-color: white; padding: 2px">
                        ${areaDimentions.width} km
                    </span>`
                )
        }
        else {
            areaMarkerRef.current = L.tooltip({ permanent: true, direction: 'center' })
            areaMarkerRef.current.setLatLng(areaCenter)
            areaMarkerRef.current.setContent(
                `<span style="font-weight: bold; font-size: 14px; z-indez: 1; background-color: white; padding: 2px">
                    ${areaDimentions.area} km²
                </span>`
            )
            mapRef.current.addLayer(areaMarkerRef.current) 

            heightMarkerRef.current = L.tooltip({ permanent: true, direction: 'center' })
            heightMarkerRef.current.setLatLng(midRight)
            heightMarkerRef.current.setContent(
                `<span style="font-weight: bold; font-size: 14px; display: inline-block; transform: rotate(-90deg); z-indez: 1; background-color: white; padding: 2px">
                    ${areaDimentions.height} km
                </span>`
            )
            mapRef.current.addLayer(heightMarkerRef.current ) 

            widthMarkerRef.current = L.tooltip({ permanent: true, direction: 'center' })
            widthMarkerRef.current.setLatLng(midTop)
            widthMarkerRef.current.setContent(
                `<span style="font-weight: bold; font-size: 14px; display: inline-block; z-indez: 1; background-color: white; padding: 2px">
                    ${areaDimentions.width} km
                </span>`
            )
            mapRef.current.addLayer(widthMarkerRef.current) 
        }
    }

    const calculateRectangeDimentionsInKm = (latlngs) => {
        const bottomLeft = L.latLng(latlngs[0]);
        const topLeft = L.latLng(latlngs[1]);
        const topRight = L.latLng(latlngs[2]);

        const width = (topLeft.distanceTo(topRight) / 1000).toFixed(2);
        const height = (topLeft.distanceTo(bottomLeft) / 1000).toFixed(2);

        return {
            width: width,
            height: height,
            area: (width * height).toFixed(2)
        }
    }

    const getMidPoint = (latlng1, latlng2) => {
        const lat1 = latlng1.lat * Math.PI / 180;
        const lon1 = latlng1.lng * Math.PI / 180;
        const lat2 = latlng2.lat * Math.PI / 180;
        const lon2 = latlng2.lng * Math.PI / 180;

        const Bx = Math.cos(lat2) * Math.cos(lon2 - lon1);
        const By = Math.cos(lat2) * Math.sin(lon2 - lon1);
        const lat3 = Math.atan2(
            Math.sin(lat1) + Math.sin(lat2),
            Math.sqrt((Math.cos(lat1) + Bx) * (Math.cos(lat1) + Bx) + By * By)
        );
        const lon3 = lon1 + Math.atan2(By, Math.cos(lat1) + Bx);

        return {
            lat: lat3 * 180 / Math.PI,
            lng: lon3 * 180 / Math.PI
        };
    }

    return (
        <div id='map' className='w-[25%] h-[30rem] fixed right-[2%]'>
        </div>
    )
}